import { ToastrService } from 'ngx-toastr';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ChangeDetectorRef,
} from '@angular/core';
import { Sort, SortDirection } from '@angular/material/sort';

import { CameraEventsService } from '@app/dashboard/cameras/services/camera-events.service';
import { ISnFeedQueryParameters, ISnListQueryParameters, ISnListResponse } from '@lib/api';
import { SnCamera } from '@lib/api/entities';
import { LanguageSelectionService, t } from '@lib/translation';
import { SnActionConfig } from '@lib/ui/actions';
import { SnDataListComponent } from '@lib/ui/data-list';
import { ISnListItem } from '@lib/ui/data-list';
import { SnDialogService } from '@lib/ui/dialog';

import { formatBandwidth } from '../../helpers/utils';

type CameraUpdateProps = 'record' | 'loopDuration' | 'quality' | 'path';

@Component({
  selector: 'fs-appliance-dashboard-cameras',
  templateUrl: './appliance-dashboard-cameras.component.html',
  styleUrls: ['./appliance-dashboard-cameras.component.scss'],
})
export class ApplianceDashboardCamerasComponent<M extends ISnListItem>
  implements OnInit, OnDestroy
{
  private readonly DEFAULT_RETENTION_DAYS_OPTIONS = Array.from({ length: 90 }, (_, i) => i + 1);

  @Input()
  public applianceId: string;

  @Input()
  public actions!: SnActionConfig<SnCamera>[];

  @Input()
  public fetchFn: (params: ISnListQueryParameters) => Observable<ISnListResponse<M>>;

  @Input()
  public feedFn: (params: ISnFeedQueryParameters) => Observable<M[]>;

  @Input()
  public bandwidths: { [cameraId: string]: number } = {};

  @Input()
  public sort: string = 'name';

  @Input()
  public sortDirection: SortDirection = 'asc';

  @Input()
  public retentionDaysOptions: number[] = this.DEFAULT_RETENTION_DAYS_OPTIONS;

  @Output()
  public readonly sortChange = new EventEmitter<Sort>();

  @Output()
  public readonly publish = new EventEmitter<{
    cameras: { [id: string]: Partial<SnCamera> };
    onSuccess: () => void;
    onFinally: () => void;
  }>();

  @Output()
  public readonly cancel = new EventEmitter<void>();

  @Output()
  public readonly capacityChange = new EventEmitter<{}>();

  @Output()
  public camerasDataReady = new EventEmitter<void>();

  @ViewChild('listElement', { static: true })
  public list!: SnDataListComponent<SnCamera>;

  public isLoading: boolean = false;

  private readonly QUALITY_LOWEST_KEY = 'Lowest ';
  private readonly QUALITY_HIGHEST_KEY = 'Highest ';
  private readonly QUALITY_FPS_KEY = 'fps';
  private readonly QUALITY_RESOLUTION_DELIMITER = 'x';
  private readonly MINS_IN_DAY = 1440;
  private readonly H264_CAMERAS = 'H264';

  public get columns(): string[] {
    return this._getColumns();
  }

  protected readonly Math = Math;

  private _editedCameras: { [id: string]: Partial<SnCamera> } = {};
  private _qualityOptionsMap: { [cameraId: string]: { [quality: string]: string } } = {};
  private readonly _destroy$ = new Subject<void>();

  constructor(
    private readonly _toastrService: ToastrService,
    private readonly _cd: ChangeDetectorRef,
    private readonly _cameraEventsService: CameraEventsService,
    private readonly _dialogService: SnDialogService,
    private readonly _languageService: LanguageSelectionService,
  ) {}

  ngOnInit(): void {
    this._initiateCameraEventSubscriptions();
    this.list.reference.dataSource.updated$.pipe(takeUntil(this._destroy$)).subscribe(() => {
      this._processCamerasData();
      this.camerasDataReady.emit();
    });
  }

  public ngOnDestroy(): void {
    this.publish.complete();
    this.cancel.complete();

    this.sortChange.complete();

    this._destroy$.next();
    this._destroy$.complete();
  }

  private _updateCamerasOnSave() {
    Object.keys(this._editedCameras).forEach((cameraId: string) => {
      this._updateCamera(cameraId, this._editedCameras[cameraId]);
    });
    this._editedCameras = {};
  }

  private _processCamerasData(): void {
    this._updateQualityOptions(this.list.dataSource.data);
  }

  private _updateQualityOptions(cameras: SnCamera[]): void {
    cameras.forEach((camera) => {
      const profiles = camera.cameraMeta?.deviceInfo?.data?.profiles || [];

      const qualityOptions: { [quality: string]: string } = {};

      profiles.forEach((profile: any) => {
        const encoder = profile.video?.encoder;
        if (
          profile.path != null &&
          (encoder.encoding === this.H264_CAMERAS || camera.path === profile.path) && //  get H264 encoded profile only unless it is the selected profile for the camera
          encoder?.resolution?.width &&
          encoder?.resolution?.height &&
          encoder?.framerate
        ) {
          const qualityKey = `${encoder.resolution.width}${this.QUALITY_RESOLUTION_DELIMITER}${encoder.resolution.height} ${encoder.framerate}${this.QUALITY_FPS_KEY}`;
          if (!(qualityKey in qualityOptions) || qualityOptions[qualityKey] !== camera.path) {
            qualityOptions[qualityKey] = profile.path;
          }
        }
      });

      const sortedOptionKeys = this._sortQualityOptions(qualityOptions);

      this._qualityOptionsMap[camera.id] = this._prepareQualityOptions(
        sortedOptionKeys,
        qualityOptions,
      );
    });
  }

  private _prepareQualityOptions(
    sortedOptionKeys: string[],
    qualityOptions: { [p: string]: string },
  ) {
    // Aggregate options and append "Lowest" and "Highest" tags
    const sortedQualityOptions: { [quality: string]: string } = {};
    sortedOptionKeys.forEach((key, index) => {
      let tag: string = '';
      if (sortedOptionKeys.length > 1) {
        tag =
          index === 0
            ? this.QUALITY_LOWEST_KEY
            : index === sortedOptionKeys.length - 1
            ? this.QUALITY_HIGHEST_KEY
            : '';
      }
      sortedQualityOptions[tag + key] = qualityOptions[key];
    });
    return sortedQualityOptions;
  }

  private _sortQualityOptions(qualityOptions: { [p: string]: string }) {
    // Sort quality options by resolution (width, height) and FPS in ascending order
    return Object.keys(qualityOptions).sort((a, b) => {
      const [resA, fpsA] = a.split(' ');
      const [resB, fpsB] = b.split(' ');
      const [widthA, heightA] = resA.split(this.QUALITY_RESOLUTION_DELIMITER).map(Number);
      const [widthB, heightB] = resB.split(this.QUALITY_RESOLUTION_DELIMITER).map(Number);
      const fpsNumA = parseInt(fpsA.replace(this.QUALITY_FPS_KEY, ''), 10);
      const fpsNumB = parseInt(fpsB.replace(this.QUALITY_FPS_KEY, ''), 10);

      return widthA - widthB || heightA - heightB || fpsNumA - fpsNumB;
    });
  }

  public getQualityOptions(cameraId: string): string[] {
    return Object.keys(this._qualityOptionsMap[cameraId] || {});
  }

  public getCameraQuality(cameraId: string, path: string): string | null {
    const options = this._qualityOptionsMap[cameraId] || {};
    return Object.keys(options).find((quality) => options[quality] === path) || null;
  }

  public isEditedCameras(): boolean {
    return Object.keys(this._editedCameras).length === 0;
  }

  private _onFieldChange(cameraId: string, field: CameraUpdateProps, value: any): void {
    if (!this._editedCameras[cameraId]) {
      this._editedCameras[cameraId] = { [field]: value };
    } else {
      const currentCamera = this.list.dataSource.data.find((camera) => camera.id === cameraId);
      // check if user updated value is same as user original camera
      if (currentCamera && value === currentCamera[field]) {
        delete this._editedCameras[cameraId][field];
      } else {
        this._editedCameras[cameraId][field] = value;
      }

      if (Object.keys(this._editedCameras[cameraId]).length === 0) {
        delete this._editedCameras[cameraId];
      }
    }
  }

  public onRecordChange(cameraId: string, isRecord: any): void {
    this._emitCapacityChange(cameraId, 'record', isRecord);
    this._onFieldChange(cameraId, 'record', isRecord);
  }

  public onLoopDurationChange(cameraId: string, retentionDays: any): void {
    const retentionMins = retentionDays * this.MINS_IN_DAY;
    this._emitCapacityChange(cameraId, 'loopDuration', retentionMins);
    this._onFieldChange(cameraId, 'loopDuration', retentionMins);
  }

  public onQualityChange(cameraId: string, qualityKey: any): void {
    this._emitCapacityChange(cameraId, 'quality', qualityKey);
    const selectedPath = this._qualityOptionsMap[cameraId][qualityKey];
    this._onFieldChange(cameraId, 'path', selectedPath);
  }

  private _emitCapacityChange(cameraId: string, field: CameraUpdateProps, value: any): void {
    const currentCamera = this.list.dataSource.data.find((camera) => camera.id === cameraId);
    const bandwidth = this.bandwidths[cameraId] ?? 0;
    // get the current value if camera details are edited if not get from camera details
    const loopDuration = this._editedCameras[cameraId]?.loopDuration ?? currentCamera?.loopDuration;
    const record = this._editedCameras[cameraId]?.record ?? currentCamera?.record;

    if (field === 'record') {
      this.capacityChange.emit({
        cameraId,
        record: value,
        bandwidth,
        loopDuration,
      });
    } else if (field === 'loopDuration' && record) {
      this.capacityChange.emit({
        cameraId,
        bandwidth,
        loopDuration,
        updatedLoopDuration: value,
      });
    } else if (field === 'quality') {
      const path = this._editedCameras[cameraId]?.path ?? currentCamera?.path;
      const oldQualityKey = Object.keys(this._qualityOptionsMap[cameraId]).find(
        (key) => this._qualityOptionsMap[cameraId][key] === path,
      );

      this.capacityChange.emit({
        cameraId,
        quality: oldQualityKey ? this._prepareQuality(oldQualityKey) : 0,
        updatedQuality: this._prepareQuality(value),
        loopDuration: record ? loopDuration : 0,
      });
    }
  }

  private _prepareQuality(qualityKey: string) {
    // Extract resolution and framerate from the quality string
    const parsedQualityKey = qualityKey
      .replace(this.QUALITY_LOWEST_KEY, '')
      .replace(this.QUALITY_HIGHEST_KEY, '');
    const [resolution, framerateStr] = parsedQualityKey.split(' ');
    const [width, height] = resolution.split(this.QUALITY_RESOLUTION_DELIMITER).map(Number);
    const framerate = parseInt(framerateStr.replace(this.QUALITY_FPS_KEY, ''), 10);
    return {
      resolution: { width, height },
      framerate,
    };
  }

  public publishChanges(): void {
    const isRecordingDisabled: boolean = Object.values(this._editedCameras).some(
      (camera) => camera.record === false,
    );

    if (isRecordingDisabled) {
      this._dialogService
        .confirm({
          title: t('appliances.dialogs.deleteRecordedData.title'),
          message: t('appliances.dialogs.deleteRecordedData.message'),
          confirmButton: { text: t('appliances.dialogs.deleteRecordedData.deleteButton'), color: 'warn' },
          cancelButton: { text: t('common.cancel').toUpperCase() },
          confirmationInput: {
            text: t('common.confirm').toUpperCase(),
            label: t('appliances.dialogs.deleteRecordedData.confirmationLabel'),
            placeholder: t('appliances.dialogs.deleteRecordedData.confirmationPlaceholder'),
          },
        })
        .subscribe((confirmed) => {
          if (confirmed) {
            this.executePublish();
          }
        });
    } else {
      this.executePublish();
    }
  }

  private executePublish(): void {
    this.isLoading = true;
    this.publish.next({
      cameras: this._editedCameras,
      onSuccess: () => this._updateCamerasOnSave(),
      onFinally: () => {
        this.isLoading = false;
        this._cd.markForCheck();
      },
    });
  }

  public discardChanges(): void {
    this._editedCameras = {};
    this._reloadData();
    this._toastrService.info(t('appliances.messages.changesDiscarded'));
    if (this.cancel) {
      this.cancel.emit();
    }
  }

  public trackBy(_index: number, camera: SnCamera): any {
    return camera.id;
  }

  private _getColumns(): string[] {
    return [
      'name',
      'make',
      'model',
      'status',
      'bandwidth',
      'quality',
      'recording',
      'videoRetention',
      'actions',
    ];
  }

  public onSortChange(sort: Sort): void {
    this.sortChange.emit(sort);
  }

  private _reloadData(): void {
    this.list.reference.reload();
  }

  private _updateCamera(cameraId: string, cameraUpdatePropValues: Partial<SnCamera>): void {
    const targetCamera = this.list.dataSource.data.find((c) => c.id === cameraId);
    if (targetCamera) {
      if (cameraUpdatePropValues.record != null) {
        targetCamera.record = cameraUpdatePropValues.record;
      }
      if (cameraUpdatePropValues.path != null) {
        targetCamera.path = cameraUpdatePropValues.path;
      }
      if (cameraUpdatePropValues.loopDuration != null) {
        targetCamera.loopDuration = cameraUpdatePropValues.loopDuration;
      }
      this.list.dataSource.updateItem(targetCamera);
    }
  }

  public getRetentionDays(camera: SnCamera): number {
    const loopDuration = this._editedCameras[camera.id]?.loopDuration ?? camera?.loopDuration;
    return loopDuration != null ? Math.floor(loopDuration / this.MINS_IN_DAY) : 0;
  }

  public formatBandwidth(bps: number): string {
    const locale = this._languageService.getCurrentLocale();
    return formatBandwidth(bps, 0, locale);
  }

  private _initiateCameraEventSubscriptions() {
    this._cameraEventsService.created$.pipe(takeUntil(this._destroy$)).subscribe((camera) => {
      this.list.dataSource.insertItemToStart(camera);
    });

    this._cameraEventsService.updated$.pipe(takeUntil(this._destroy$)).subscribe((camera) => {
      this.list.dataSource.updateItem(camera);
    });

    this._cameraEventsService.deleted$.pipe(takeUntil(this._destroy$)).subscribe((camera) => {
      this.list.dataSource.deleteItem(camera);
    });
  }

  public isVideoRetentionEnabledForCamera(camera: SnCamera): boolean {
    return (
      (this._editedCameras[camera.id]?.record ?? camera.record) &&
      this.retentionDaysOptions?.length > 0
    );
  }
}
